import { memo, useMemo } from 'react';
import styled from 'styled-components';
import ChangesList from './ChangesList';
import AssistantAnswer from './AssistantAnswer';
import SizeFilter from './SizeFilter';
import { anyOne, countTitle, isReindeer, plural } from './subjectWords';

/** The side panel of the results view (the counting-video design): mode, count, size distribution, count over time. */

// "12 cars · 3 trucks": each kind's count in its colour, under the count (several kinds)
const KindLine = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${({ theme }) => `${theme.token.marginXXS}px ${theme.token.marginSM}px`};
	margin-top: ${({ theme }) => theme.token.marginXS}px;
	font-size: ${({ theme }) => theme.token.fontSizeSM}px;
	color: ${({ theme }) => theme.token.colorText};
	span {
		display: inline-flex;
		align-items: center;
		gap: ${({ theme }) => theme.token.marginXXS}px;
	}
	i {
		width: ${({ theme }) => theme.token.sizeXS}px;
		height: ${({ theme }) => theme.token.sizeXS}px;
		border-radius: 50%;
	}
`;

const Panel = styled.aside`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.25rem 1.4rem;
	border-radius: 18px;
	border: 1px solid rgba(60, 60, 60, 0.9);
	background: rgba(25, 25, 25, 0.78);
	backdrop-filter: blur(14px);
	color: ${({ theme }) => theme.colors.textColor};
	min-width: 0;
`;

const Title = styled.h3`
	margin: 0;
	font-size: 1.25rem;
	font-weight: 700;
	color: #f5f5f5;
`;

export const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.4rem;
`;

export const Chip = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${({ theme }) => theme.token.marginXXS}px;
	white-space: nowrap;
	border: 1px solid
		${({ $active, theme }) =>
			$active ? theme.colors.darkGreen : theme.colors.backgroundAccent};
	background: ${({ $active, theme }) => ($active ? theme.colors.darkGreen : 'transparent')};
	color: ${({ $active }) => ($active ? '#fff' : '#b0b0b0')};
	border-radius: 999px;
	padding: 0.25rem 0.75rem;
	font-size: 0.82rem;
	font-weight: 600;
	cursor: pointer;
	&:hover {
		border-color: ${({ theme }) => theme.colors.green};
	}
`;

const Big = styled.div`
	font-size: 4.2rem;
	line-height: 1;
	font-weight: 800;
	color: #f5f5f5;
	font-variant-numeric: tabular-nums;
`;

const Sub = styled.div`
	color: ${({ theme }) => theme.colors.textColor};
	font-size: 0.95rem;
	margin-top: 0.35rem;
`;

const Note = styled.div`
	color: ${({ $warn }) => ($warn ? '#e0a030' : '#9a9a9a')};
	font-size: 0.78rem;
	margin-top: 0.35rem;
`;

const Rule = styled.hr`
	border: 0;
	border-top: 1px solid rgba(70, 70, 70, 0.9);
	margin: 0;
	opacity: 1;
`;

const Stats = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;
	b {
		display: block;
		font-size: 1.7rem;
		color: #f5f5f5;
		font-variant-numeric: tabular-nums;
	}
	span {
		color: #a0a0a0;
		font-size: 0.8rem;
	}
`;

const H = styled.div`
	font-weight: 600;
	color: #f5f5f5;
	font-size: 0.95rem;
	margin-bottom: 0.5rem;
`;

const Legend = styled.div`
	display: flex;
	gap: 1.2rem;
	font-size: 0.85rem;
	margin-top: 0.4rem;
	i {
		display: inline-block;
		width: 11px;
		height: 11px;
		margin-right: 6px;
		vertical-align: -1px;
		background: ${({ theme }) => theme.colors.green};
	}
	i.calf {
		background: ${({ theme }) => theme.colors.lightGreen};
	}
`;

const Foot = styled.div`
	margin-top: auto;
	color: #9a9a9a;
	font-size: 0.78rem;
`;

// axis value: one decimal below 10 m, whole numbers otherwise
const sizeLabel = (v, unit) => `${unit === 'm' && v < 10 ? v.toFixed(1) : Math.round(v)} ${unit}`;

const Histogram = ({ hist }) => {
	const { calf, adult, hi, unit } = hist;
	const peak = Math.max(1, ...calf.map((c, i) => c + adult[i]));
	const W = 300;
	const Hh = 90;
	const bw = W / calf.length;
	return (
		<svg
			viewBox={`0 0 ${W} ${Hh + 16}`}
			width="100%"
			role="img"
			aria-label={`Size distribution in ${unit === 'm' ? 'metres' : 'pixels'}`}>
			{calf.map((c, i) => {
				const ha = (Hh * adult[i]) / peak;
				const hc = (Hh * c) / peak;
				return (
					<g key={i}>
						{adult[i] > 0 && (
							<rect
								x={i * bw + 1.5}
								y={Hh - ha}
								width={bw - 3}
								height={ha}
								fill="#20AC6B"
							/>
						)}
						{c > 0 && (
							<rect
								x={i * bw + 1.5}
								y={Hh - ha - hc}
								width={bw - 3}
								height={hc}
								fill="#90d7ad"
							/>
						)}
					</g>
				);
			})}
			<line x1={0} x2={W} y1={Hh} y2={Hh} stroke="#5a5a5a" strokeWidth="1" />
			<text x={0} y={Hh + 13} fill="#9a9a9a" fontSize="10">
				0
			</text>
			{hist.total > 0 && (
				<>
					<text x={W / 2} y={Hh + 13} fill="#9a9a9a" fontSize="10" textAnchor="middle">
						{sizeLabel(hi / 2, unit)}
					</text>
					<text x={W} y={Hh + 13} fill="#9a9a9a" fontSize="10" textAnchor="end">
						{sizeLabel(hi, unit)}
					</text>
				</>
			)}
		</svg>
	);
};

// what the sizes are and how far to trust them
// "reindeer" → "Reindeer"; calves are told apart for reindeer only (the size cut was measured on herds)

const sizeNote = (hist, calves) => {
	const calf = calves ? ' Calf-sized: small for the herd at the same distance.' : '';
	// animals have a body; a car or a person has a length
	const body = calves ? 'Body length' : 'Length';
	if (hist.unit !== 'm')
		return hist.flightWithoutHeight
			? `${body} in pixels: the flight data has no height above the ground to give metres.${calf}`
			: `${body} in pixels: this video has no flight data to give metres.${calf}`;
	const what = hist.across
		? `${calves ? 'Body width' : 'Width'} across the image, in metres on the ground (the camera is tilted)`
		: `${body} in metres on the ground`;
	const height =
		hist.heightRef === 'takeoff'
			? ', from the flight height above the take-off point (estimate)'
			: ' (estimate)';
	return `${what}${height}.${calf}`;
};

const Sparkline = ({ series, frame }) => {
	const W = 300;
	const Hh = 70;
	const n = series?.length || 0;
	const path = useMemo(() => {
		if (!n) return null;
		const max = Math.max(1, ...series);
		const step = Math.max(1, Math.floor(n / 300));
		const pts = [];
		for (let i = 0; i < n; i += step)
			pts.push([(i / Math.max(1, n - 1)) * W, Hh - (Hh * series[i]) / max]);
		pts.push([W, Hh - (Hh * series[n - 1]) / max]);
		return { line: pts.map(p => p.join(',')).join(' '), max };
	}, [series, n]);
	if (!path) return null;
	const x = (frame / Math.max(1, n - 1)) * W;
	return (
		<svg viewBox={`0 0 ${W} ${Hh + 2}`} width="100%" role="img" aria-label="Count over time">
			<defs>
				<clipPath id="played">
					<rect x="0" y="0" width={x} height={Hh + 2} />
				</clipPath>
			</defs>
			<polyline points={path.line} fill="none" stroke="#3b4a42" strokeWidth="2" />
			<g clipPath="url(#played)">
				<polygon points={`0,${Hh} ${path.line} ${W},${Hh}`} fill="#18462f" />
				<polyline points={path.line} fill="none" stroke="#20AC6B" strokeWidth="2.5" />
			</g>
			<line x1={x} x2={x} y1={0} y2={Hh} stroke="#f5f5f5" strokeWidth="1" opacity="0.6" />
			<line x1={0} x2={W} y1={Hh} y2={Hh} stroke="#5a5a5a" strokeWidth="1" />
		</svg>
	);
};

const SliderRow = styled.label`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin: 0;
	font-size: 0.82rem;
	color: #f5f5f5;
	font-weight: 600;
	input {
		width: 100%;
		accent-color: ${({ theme }) => theme.colors.green};
	}
`;

/** Confidence slider: hides animals whose best detection is below the value; the stored count is the lowest value. */
const Sensitivity = ({ value, min, onChange }) => (
	<SliderRow>
		<span style={{ display: 'flex', justifyContent: 'space-between' }}>
			Sensitivity
			<span style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(value * 100)} %</span>
		</span>
		<input
			type="range"
			min={min}
			max={0.95}
			step={0.01}
			value={value}
			onChange={e => onChange(Number(e.target.value))}
			aria-label="Minimum detection confidence"
			aria-valuetext={`${Math.round(value * 100)} percent`}
		/>
		<span
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				fontWeight: 400,
				fontSize: '0.72rem',
				color: '#9a9a9a',
			}}>
			<span>Find more</span>
			<span>Fewer false alarms</span>
		</span>
	</SliderRow>
);

const MODE_LABEL = { hover: 'Total count', line: 'Line crossing', max: 'Most in view' };

/** "14 removed · 2 added · 1 merged by you", with the edits newest first and an undo for each. */

/** The assistant's proposals, to accept one by one or all at once; nothing counts until accepted. */

const CountPanel = ({
	modes,
	mode,
	onModeChange,
	count,
	subtitle,
	note,
	noteWarn,
	inView,
	maxInView,
	hist,
	series,
	frame,
	modelLabel,
	sensitivity,
	subject = '',
	changes = null,
	edits = null,
	onUndo = null,
	onAccept = null,
	onRetry = null,
	// the assistant's question while correcting (AssistantCard), above its proposals
	assistant = null,
	// the size filter (useCountModel.sizeFilter)
	sizeFilter = null,
	// the job following added objects, while one runs (corrections layer `follow`)
	follow = null,
	// several kinds: how many of each ([{ kind, n }]), their colours, and the broad kind asked for ("vehicle")
	byKind = null,
	kindColors = null,
	category = null,
	// the last Look again in this visit: how many it kept, and when
	answerKept = null,
	answerAt = '',
	fps = 30,
	onRunAgain = null,
}) => {
	const proposals = (edits || []).filter(e => e.status === 'proposed');
	const hasChanges = (edits || []).some(e =>
		['ready', 'following', 'failed', 'unmatched'].includes(e.status)
	);
	return (
		<Panel>
			<Title>{countTitle(category || subject)}</Title>
			<Chips role="tablist" aria-label="Counting mode">
				{modes.map(m => (
					<Chip
						key={m}
						type="button"
						role="tab"
						aria-selected={m === mode}
						$active={m === mode}
						onClick={() => onModeChange(m)}>
						{MODE_LABEL[m]}
					</Chip>
				))}
			</Chips>
			<div>
				<Big>{count ?? '—'}</Big>
				<Sub>{subtitle}</Sub>
				{byKind?.length > 0 && (
					<KindLine>
						{byKind.map(({ kind, n }) => (
							<span key={kind}>
								<i style={{ background: kindColors?.[kind]?.hex }} />
								{n} {n === 1 ? kind : plural(kind)}
							</span>
						))}
					</KindLine>
				)}
				{note && <Note $warn={noteWarn}>{note}</Note>}
			</div>
			{assistant && (
				<>
					<Rule />
					{assistant}
				</>
			)}
			{proposals.length > 0 && (
				<>
					<Rule />
					<AssistantAnswer
						proposals={proposals}
						subject={anyOne(subject)}
						at={answerAt}
						kept={answerKept}
						onAccept={onAccept}
						onDismiss={onUndo}
					/>
				</>
			)}
			{hasChanges && (
				<>
					<Rule />
					<ChangesList
						changes={changes}
						edits={edits}
						subject={anyOne(subject)}
						fps={fps}
						follow={follow}
						onUndo={onUndo}
						onRetry={onRetry}
						onRunAgain={onRunAgain}
					/>
				</>
			)}
			{sensitivity?.available && (
				<>
					<Rule />
					<Sensitivity {...sensitivity} />
				</>
			)}
			<Rule />
			<Stats>
				<div>
					<b>{inView}</b>
					<span>In view now</span>
				</div>
				<div>
					<b>{maxInView}</b>
					<span>Most in view at once</span>
				</div>
			</Stats>
			<Rule />
			<div>
				<H>Size of what was counted</H>
				<Histogram
					hist={
						isReindeer(subject)
							? hist
							: {
									...hist,
									calf: hist.calf.map(() => 0),
									adult: hist.calf.map((c, i) => c + hist.adult[i]),
								}
					}
				/>
				{isReindeer(subject) ? (
					<Legend>
						<span>
							<i className="calf" />
							{hist.nCalf} calf-sized
						</span>
						<span>
							<i />
							{hist.total - hist.nCalf} adult-sized
						</span>
					</Legend>
				) : (
					<Legend>
						<span>
							<i />
							{hist.total} counted
						</span>
					</Legend>
				)}
				<Note>{sizeNote(hist, isReindeer(subject))}</Note>
				{sizeFilter && (
					<SizeFilter
						{...sizeFilter}
						colors={kindColors}
						subject={anyOne(subject)}
						onChange={sizeFilter.setRange}
						onReset={sizeFilter.reset}
					/>
				)}
			</div>
			<Rule />
			<div>
				<H>Count over time</H>
				<Sparkline series={series} frame={frame} />
			</div>
			<Foot>{modelLabel}</Foot>
		</Panel>
	);
};

export default memo(CountPanel);
